const normalizeText = (value = '') => String(value || '').trim();

const buildReferenceCode = (serviceId = '') => {
  const clean = normalizeText(serviceId).replace(/[^a-z0-9]/gi, '').toUpperCase();
  return `SRV-${clean.slice(0, 8) || 'PEND'}`;
};

export const buildServiceAddressText = (snapshot = {}) =>
  [
    snapshot?.address,
    snapshot?.apartment,
    snapshot?.locality,
    snapshot?.partido,
    snapshot?.province,
    snapshot?.postalCode,
  ]
    .map((part) => normalizeText(part))
    .filter(Boolean)
    .join(', ');

export const buildServiceRouteDestinations = (service, { originLatlng, destinationLatlng, additionalStopsLatlng = [] } = {}) => {
  const serviceId = normalizeText(service?.id);
  const clientName = normalizeText(service?.clientNameSnapshot || service?.clientName || 'Cliente');
  const clientContactName = normalizeText(service?.clientContactPersonSnapshot || service?.contactPersonSnapshot || clientName);
  const clientContactPhone = normalizeText(service?.clientPhoneSnapshot || service?.contactPhoneSnapshot || '');
  const serviceTransportLabel = normalizeText(service?.serviceTransportLabel || service?.serviceTypeLabel || service?.serviceTransport || service?.serviceType || 'Camioneta');
  const serviceTaskLabel = normalizeText(service?.serviceTaskLabel || service?.serviceTask || 'Servicio');
  const originSnapshot = service?.originSnapshot || {};
  const isOfficeOrigin = String(originSnapshot?.source || '').trim().toLowerCase() === 'office';
  const destinationSnapshot = service?.destinationSnapshot || {};
  const additionalStops = Array.isArray(service?.additionalStops) ? service.additionalStops : [];
  const referenceCode = buildReferenceCode(serviceId);

  const originId = `service-${serviceId || Date.now()}-origin`;
  const deliveryStops = [
    {
      id: `service-${serviceId || Date.now()}-destination`,
      snapshot: destinationSnapshot,
      latlng: destinationLatlng || destinationSnapshot?.latlng || null,
    },
    ...additionalStops.map((stop, index) => ({
      id: `service-${serviceId || Date.now()}-stop-${index + 1}`,
      snapshot: stop,
      latlng: additionalStopsLatlng[index] || stop?.latlng || null,
    })),
  ];
  const resolveStopTaskLabel = (snapshot = {}) =>
    normalizeText(snapshot?.serviceActionLabel || snapshot?.serviceTaskLabel || service?.serviceTaskLabel || service?.serviceTask || 'Servicio');
  const resolveStopTaskValue = (snapshot = {}) =>
    normalizeText(snapshot?.serviceAction || snapshot?.serviceTask || service?.serviceTask || '');

  const originDestination = {
      id: originId,
      name: normalizeText(originSnapshot?.label || service?.origin || clientName || 'Retiro'),
      address: normalizeText(originSnapshot?.address || ''),
      apartment: normalizeText(originSnapshot?.apartment || ''),
      locality: normalizeText(originSnapshot?.locality || ''),
      province: normalizeText(originSnapshot?.province || 'CABA') || 'CABA',
      partido: normalizeText(originSnapshot?.partido || ''),
      postalCode: normalizeText(originSnapshot?.postalCode || ''),
      type: 'Retiro',
      contactType: 'cliente',
      contactDocId: normalizeText(service?.clientId || ''),
      deliveries: 0,
      pickups: deliveryStops.length,
      deliversTo: deliveryStops.map((stop) => stop.id).join(','),
      operationRole: 'pickup',
      operationalLoads: deliveryStops.map((stop, index) => ({
        id: `service-${serviceId || Date.now()}-load-${index + 1}`,
        title: `${resolveStopTaskLabel(stop.snapshot)} · ${clientName}`,
        description: normalizeText(service?.notes || ''),
        quantity: 1,
        unit: 'unidad',
        operationType: 'pickup',
        status: 'planned',
        category: 'servicio',
        referenceCode,
        originStopId: originId,
        targetStopId: stop.id,
        currentStopId: null,
        requiresEvidenceOnPickup: false,
        requiresEvidenceOnDelivery: false,
        pickupEvidence: [],
        deliveryEvidence: [],
        notes: normalizeText(service?.notes || ''),
        serviceId,
        serviceMode: normalizeText(service?.serviceMode || ''),
        serviceModeLabel: normalizeText(service?.serviceModeLabel || ''),
        salePrice: Number(service?.salePrice || 0) || 0,
      })),
      incomingOperationalLoads: [],
      priority: normalizeText(service?.priorityLabel || service?.priority || 'Normal') || 'Normal',
      schedule: normalizeText(service?.scheduledDate || service?.date || ''),
      contactName: clientContactName,
      contactPhone: clientContactPhone,
      latlng: originLatlng || originSnapshot?.latlng || null,
      serviceId,
      serviceLinked: true,
      serviceType: normalizeText(service?.serviceType || service?.serviceTransport || ''),
      serviceTypeLabel: serviceTransportLabel,
      serviceTransport: normalizeText(service?.serviceTransport || service?.serviceType || ''),
      serviceTransportLabel,
      serviceTask: normalizeText(service?.serviceTask || ''),
      serviceTaskLabel,
      serviceMode: normalizeText(service?.serviceMode || ''),
      serviceModeLabel: normalizeText(service?.serviceModeLabel || ''),
      serviceReferenceCode: referenceCode,
      serviceSalePrice: Number(service?.salePrice || 0) || 0,
      serviceNotes: normalizeText(service?.notes || ''),
      isOperationalOrigin: isOfficeOrigin,
    };

  const deliveryDestinations = deliveryStops.map((stop, index) => ({
      id: stop.id,
      name: normalizeText(stop.snapshot?.label || (index === 0 ? service?.destination : `Parada ${index}`) || clientName || 'Entrega'),
      address: normalizeText(stop.snapshot?.address || ''),
      apartment: normalizeText(stop.snapshot?.apartment || ''),
      locality: normalizeText(stop.snapshot?.locality || ''),
      province: normalizeText(stop.snapshot?.province || 'CABA') || 'CABA',
      partido: normalizeText(stop.snapshot?.partido || ''),
      postalCode: normalizeText(stop.snapshot?.postalCode || ''),
      type: resolveStopTaskLabel(stop.snapshot),
      contactType: 'cliente',
      contactDocId: normalizeText(service?.clientId || ''),
      deliveries: 1,
      pickups: 0,
      deliversTo: '',
      operationRole: 'delivery',
      operationalLoads: [],
      incomingOperationalLoads: [
        {
          loadId: `service-${serviceId || Date.now()}-load-${index + 1}`,
          sourceDestinationId: isOfficeOrigin ? '' : originId,
          sourceDestinationName: normalizeText(originSnapshot?.label || service?.origin || 'Retiro'),
          title: `${resolveStopTaskLabel(stop.snapshot)} · ${clientName}`,
          quantity: 1,
          unit: 'unidad',
          operationType: 'pickup',
        },
      ],
      priority: normalizeText(service?.priorityLabel || service?.priority || 'Normal') || 'Normal',
      schedule: normalizeText(service?.scheduledDate || service?.date || ''),
      contactName: clientContactName,
      contactPhone: clientContactPhone,
      latlng: stop.latlng,
      serviceId,
      serviceLinked: true,
      serviceType: normalizeText(service?.serviceType || service?.serviceTransport || ''),
      serviceTypeLabel: serviceTransportLabel,
      serviceTransport: normalizeText(service?.serviceTransport || service?.serviceType || ''),
      serviceTransportLabel,
      serviceTask: resolveStopTaskValue(stop.snapshot),
      serviceTaskLabel: resolveStopTaskLabel(stop.snapshot),
      serviceMode: normalizeText(service?.serviceMode || ''),
      serviceModeLabel: normalizeText(service?.serviceModeLabel || ''),
      serviceReferenceCode: referenceCode,
      serviceSalePrice: Number(service?.salePrice || 0) || 0,
      serviceNotes: normalizeText(service?.notes || ''),
      serviceStopIndex: index,
      serviceStopCount: deliveryStops.length,
      serviceStopNotes: normalizeText(stop.snapshot?.actionNotes || ''),
    }));

  return isOfficeOrigin ? deliveryDestinations : [originDestination, ...deliveryDestinations];
};
